import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, LogOut, Mail, XCircle, Save } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WordMark } from "@/components/brand";
import { POOL_NIGHT } from "@/constants/media";
import AuthNav from "@/components/site/AuthNav";

const inputCls = "w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-foreground focus:border-gold focus:outline-none";

export default function PendingActivation() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [edit, setEdit] = useState({ phone: "", contact_method: "email", preferred_dates: "", interest: "", why_join: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ready || !user || user.is_staff || user.account_status === "Active") return;
    api.get("/auth/request-status").then((r) => {
      setInfo(r.data);
      const rq = r.data.request || {};
      setEdit({ phone: rq.phone || "", contact_method: rq.contact_method || "email", preferred_dates: rq.preferred_dates || "", interest: rq.interest || "", why_join: rq.why_join || "" });
    }).catch(() => setInfo(false));
  }, [ready, user]);

  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_staff || user.account_status === "Active") return <Navigate to="/portal" replace />;

  const declined = ["Declined", "Suspended", "Restricted", "Withdrawn"].includes(user.account_status);
  const save = async () => {
    setSaving(true);
    try { await api.patch("/auth/request-status", edit); toast.success("Your request has been updated."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const withdraw = async () => {
    if (!window.confirm("Withdraw your access request? This cannot be undone.")) return;
    try { await api.post("/auth/request-status/withdraw"); toast.success("Your request has been withdrawn."); logout(); nav("/"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const req = info?.request;
  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="pending-activation">
      <AuthNav />
      <div className="relative hidden lg:block">
        <img src={POOL_NIGHT} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="relative z-10 h-full flex items-end p-12">
          <p className="font-serif text-3xl italic text-gold text-glow-gold max-w-sm">The finest experiences are worth the wait.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-ink overflow-y-auto">
        <div className="w-full max-w-md text-center py-8">
          <Link to="/"><WordMark size="text-4xl" /></Link>
          <div className="mt-8 mb-4 flex justify-center">
            <span className="inline-flex items-center justify-center h-14 w-14 rounded-full border border-gold/40 text-gold"><Clock size={26} /></span>
          </div>
          {declined ? (
            <>
              <h1 className="font-serif text-3xl mb-2">Access Not Available</h1>
              <p className="text-sm text-muted-foreground mb-6">Your account status is <b>{user.account_status}</b>. If you believe this is in error, please reach out to us.</p>
            </>
          ) : (
            <>
              <h1 className="font-serif text-3xl mb-2">Awaiting Approval</h1>
              <p className="text-sm text-muted-foreground mb-4">Thank you, {user.first_name || "guest"}. The D is invitation-only — a member of our team is reviewing your request and will notify you by email once activated.</p>
            </>
          )}

          {req && (
            <div className="text-left glass rounded-xl p-4 mb-5 text-sm space-y-1" data-testid="pending-request-summary">
              <p><span className="text-muted-foreground">Submitted:</span> {fmtDate(req.created_at)}</p>
              <p><span className="text-muted-foreground">Status:</span> <span className="text-gold">{req.status}</span></p>
              {req.status === "More Information Needed" && <p className="text-sky-300 text-xs">The D has requested more information. Please update the details below.</p>}
              {req.invitation_code && <p><span className="text-muted-foreground">Invitation:</span> {req.invitation_code}</p>}
            </div>
          )}

          {info?.messages?.length > 0 && (
            <div className="text-left mb-5">
              <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2">Messages from The D</p>
              {info.messages.map((m, i) => (
                <div key={i} className="glass rounded-lg px-3 py-2 text-sm mb-2"><p className="text-foreground/90">{m.subject}</p><p className="text-[10px] text-muted-foreground">{fmtDate(m.created_at)}</p></div>
              ))}
            </div>
          )}

          {!declined && req && (
            <div className="text-left glass rounded-xl p-4 mb-5 space-y-3">
              <p className="text-[10px] tracking-label uppercase text-muted-foreground">Update your request</p>
              <input className={inputCls} placeholder="Phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} data-testid="pending-edit-phone" />
              <select className={inputCls} value={edit.contact_method} onChange={(e) => setEdit({ ...edit, contact_method: e.target.value })}>
                <option value="email">Prefer email</option><option value="phone">Prefer phone</option><option value="text">Prefer text</option>
              </select>
              <input className={inputCls} placeholder="Preferred dates" value={edit.preferred_dates} onChange={(e) => setEdit({ ...edit, preferred_dates: e.target.value })} />
              <input className={inputCls} placeholder="Area of interest" value={edit.interest} onChange={(e) => setEdit({ ...edit, interest: e.target.value })} />
              <textarea rows={2} className={inputCls} placeholder="Why would you love to experience The D?" value={edit.why_join} onChange={(e) => setEdit({ ...edit, why_join: e.target.value })} data-testid="pending-edit-why" />
              <button onClick={save} disabled={saving} className="btn-gold w-full py-2 text-sm inline-flex items-center justify-center gap-2" data-testid="pending-save"><Save size={14} /> Save changes</button>
            </div>
          )}

          <a href="mailto:info@thedsocialhouses.com" className="btn-ghost-gold w-full py-3 text-sm inline-flex items-center justify-center gap-2 mb-3" data-testid="pending-contact"><Mail size={15} /> Contact The D</a>
          <div className="flex items-center justify-center gap-4 mt-2">
            {!declined && <button onClick={withdraw} className="text-xs text-rose-300/80 hover:text-rose-300 inline-flex items-center gap-1.5" data-testid="pending-withdraw"><XCircle size={13} /> Withdraw request</button>}
            <button onClick={logout} className="text-xs text-muted-foreground hover:text-gold inline-flex items-center gap-1.5" data-testid="pending-signout"><LogOut size={13} /> Sign out</button>
          </div>
        </div>
      </div>
    </div>
  );
}
